import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../api';
import {
  checkinService,
  type Checkin,
  type CheckinImage,
  getCheckinImageSrc,
} from '../../api/checkin';
import type { Employee } from '../../api/Employee';
import { tripService, type Trip } from '../../api/trips';
import type { Vehicle } from '../../api/vehicles';
import {
  Navigation2,
  Search,
  RefreshCw,
  Plus,
  Car,
  User,
  CalendarDays,
  AlignLeft,
  Image as ImageIcon,
  Clock,
  CheckCircle2,
  Archive,
  Eye,
  Camera,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  BadgeCheck,
  Loader2,
} from 'lucide-react';
import {
  PageHeader,
  Button,
  Badge,
  Input,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  showToast,
} from '../../components/ui/SharedLayout';

async function resizeImage(
  base64Str: string,
  maxWidth = 800,
  maxHeight = 800,
  quality = 0.7,
) {
  return new Promise<string>((resolve) => {
    const img = new window.Image();
    img.src = base64Str;
    img.onload = () => {
      let { width, height } = img;
      if (width > maxWidth || height > maxHeight) {
        if (width > height) {
          if (width > maxWidth) {
            height *= maxWidth / width;
            width = maxWidth;
          }
        } else if (height > maxHeight) {
          width *= maxHeight / height;
          height = maxHeight;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      } else {
        resolve(base64Str);
      }
    };
  });
}

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('pt-BR');
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return `${date.toLocaleDateString('pt-BR')} às ${date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
};

const ITEMS_PER_PAGE = 20;

const getStatusTone = (status: Checkin['status']) => {
  switch (status) {
    case 'finished':
      return 'emerald';
    case 'in_progress':
    default:
      return 'amber';
  }
};

const getStatusLabel = (status: Checkin['status']) => {
  switch (status) {
    case 'finished':
      return 'Concluído';
    case 'in_progress':
    default:
      return 'Em Andamento';
  }
};

const formatTimer = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const CheckIn = () => {
  const { setPageTitle } = useAuth();
  const [checkIns, setCheckIns] = useState<Checkin[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedCheckIn, setSelectedCheckIn] = useState<Checkin | null>(null);
  const [isViewMode, setIsViewMode] = useState(false);

  const [notes, setNotes] = useState('');
  const [checkinImage, setCheckinImage] = useState<string | null>(null);
  const [checkoutImage, setCheckoutImage] = useState<string | null>(null);

  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [currentPhotoType, setCurrentPhotoType] = useState<'inicio' | 'fim' | null>(null);
  const [capturedPhoto, setCapturedPhoto] = useState<string | null>(null);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [showCheckoutModalFor, setShowCheckoutModalFor] = useState<number | null>(null);
  const [checkoutImageTemp, setCheckoutImageTemp] = useState<string | null>(null);

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalSrc, setImageModalSrc] = useState<string | null>(null);

  const [timers, setTimers] = useState<Record<number, number>>({});
  const [archivingId, setArchivingId] = useState<number | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    setPageTitle('Check-ins do setor');
  }, [setPageTitle]);

  useEffect(() => {
    const videoEl = videoRef.current;
    if (isCameraOpen && videoEl && streamRef.current) {
      videoEl.srcObject = streamRef.current;
      videoEl.muted = true;
      videoEl.playsInline = true;
      (videoEl as any).setAttribute('webkit-playsinline', 'true');
      (videoEl as any).setAttribute('playsinline', 'true');
      void videoEl.play().catch(() => {
        setFormError('Não foi possível iniciar a visualização da câmera. Tente novamente.');
      });
    }
    return () => {
      if (!isCameraOpen && videoEl) {
        videoEl.srcObject = null;
      }
    };
  }, [isCameraOpen]);

  const sortCheckInsByDate = (items: Checkin[]) =>
    [...items].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const upsertCheckIn = (saved: Checkin) => {
    setCheckIns((prev) => sortCheckInsByDate([saved, ...prev.filter((c) => c.id !== saved.id)]));
  };

  const resolveVehicle = useCallback(
    (c: Checkin) => {
      if (c.vehicle) return c.vehicle;
      return vehicles.find((v) => v.id === c.vehicle_id);
    },
    [vehicles],
  );

  const resolveEmployee = useCallback(
    (c: Checkin) => {
      if (c.employee) return c.employee;
      return employees.find((e) => e.id === c.employee_id);
    },
    [employees],
  );

  const fetchCheckIns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await checkinService.getAll();
      const sorted = sortCheckInsByDate(data);
      setCheckIns(sorted);
      setVehicles((prev) =>
        Array.from(
          new Map(
            [...prev, ...data.filter((item) => item.vehicle).map((item) => item.vehicle!)]
              .map((vehicle) => [vehicle.id, vehicle]),
          ).values(),
        ),
      );
      setEmployees((prev) =>
        Array.from(
          new Map(
            [...prev, ...data.filter((item) => item.employee).map((item) => item.employee!)]
              .map((employee) => [employee.id, employee]),
          ).values(),
        ),
      );
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao buscar check-ins.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchTrips = useCallback(async () => {
    try {
      const list = await tripService.getAll();
      setTrips(list);
      setVehicles((prev) =>
        Array.from(
          new Map(
            [...prev, ...list.filter((t) => t.vehicle).map((t) => t.vehicle!)]
              .map((v) => [v.id, v]),
          ).values(),
        ),
      );
      setEmployees((prev) =>
        Array.from(
          new Map(
            [...prev, ...list.filter((t) => t.employee).map((t) => t.employee!)]
              .map((e) => [e.id, e]),
          ).values(),
        ),
      );
    } catch (_err) {
      setTrips([]);
    }
  }, []);

  useEffect(() => {
    void fetchCheckIns();
    void fetchTrips();
  }, [fetchCheckIns, fetchTrips]);

  useEffect(() => {
    const interval = setInterval(() => {
      setTimers((prev) => {
        const updated: Record<number, number> = { ...prev };
        checkIns.forEach((c) => {
          if (c.status === 'in_progress') {
            const start = new Date(c.date).getTime();
            updated[c.id] = Math.floor((Date.now() - start) / 1000);
          }
        });
        return updated;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [checkIns]);

  const filteredCheckIns = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return checkIns;
    return checkIns.filter((c) => {
      const v = resolveVehicle(c);
      const e = resolveEmployee(c);
      return [
        c.id.toString(),
        v?.brand || '',
        v?.license_plate || '',
        e?.name || '',
        e?.cpf || '',
        c.notes || '',
        getStatusLabel(c.status),
      ]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [checkIns, searchTerm, resolveVehicle, resolveEmployee]);

  const totalPages = Math.max(1, Math.ceil(filteredCheckIns.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(1);
  }, [filteredCheckIns.length, currentPage, totalPages]);

  const { paginatedItems, pageRange } = useMemo(() => {
    const total = filteredCheckIns.length;
    const safePage = Math.min(currentPage, totalPages);
    const start = (safePage - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    return {
      paginatedItems: filteredCheckIns.slice(start, end),
      pageRange: {
        start: total === 0 ? 0 : start + 1,
        end: Math.min(end, total),
        total,
      },
    };
  }, [filteredCheckIns, currentPage, totalPages]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const openImage = (img: CheckinImage | null | undefined) => {
    const src = getCheckinImageSrc(img);
    if (!src) return;
    setImageModalSrc(src);
    setImageModalOpen(true);
  };

  const openCheckInModal = () => {
    setShowCheckInModal(true);
    setSelectedTrip(null);
    setCheckinImage(null);
    setCheckoutImage(null);
    setNotes('');
    setFormError(null);
  };

  const startCamera = async (photoType: 'inicio' | 'fim') => {
    setCameraLoading(true);
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      setFormError(null);
      setCapturedPhoto(null);
      setCurrentPhotoType(photoType);
      setIsCameraOpen(true);
    } catch (_err) {
      streamRef.current = null;
      setIsCameraOpen(false);
      showToast({
        type: 'warning',
        title: 'Não foi possível acessar a câmera',
        description: 'Verifique as permissões do navegador e tente novamente.',
      });
    } finally {
      setCameraLoading(false);
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setIsCameraOpen(false);
    setCurrentPhotoType(null);
    setCapturedPhoto(null);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const width = videoRef.current.videoWidth;
      const height = videoRef.current.videoHeight;
      if (!width || !height) {
        setFormError('A câmera ainda está carregando. Aguarde um instante.');
        return;
      }
      canvasRef.current.width = width;
      canvasRef.current.height = height;
      const ctx = canvasRef.current.getContext('2d');
      if (ctx) {
        ctx.drawImage(videoRef.current, 0, 0, width, height);
        const photo = canvasRef.current.toDataURL('image/jpeg');
        setFormError(null);
        setCapturedPhoto(photo);
      }
    }
  };

  const handleCheckinImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setCheckinImage(ev.target?.result as string);
      setCurrentPhotoType('inicio');
    };
    reader.readAsDataURL(file);
  };

  const openCheckoutModal = (checkIn: Checkin) => {
    setSelectedCheckIn(checkIn);
    setCheckoutImageTemp(null);
    setFormError(null);
    setShowCheckoutModalFor(checkIn.id);
  };

  const closeCheckoutModal = () => {
    setShowCheckoutModalFor(null);
    setCheckoutImageTemp(null);
    setFormError(null);
  };

  const confirmPhoto = async () => {
    const photo = capturedPhoto || checkinImage;
    if (photo && currentPhotoType === 'inicio' && selectedTrip) {
      const compressed = await resizeImage(photo, 800, 800, 0.7);
      const rawBase64 = compressed.replace(/^data:image\/\w+;base64,/, '');
      setCheckinImage(compressed);
      setCurrentPhotoType('inicio');
      setShowCheckInModal(false);
      try {
        const novo = await checkinService.create({
          vehicle_id: Number(selectedTrip.vehicle_id),
          employee_id: Number(selectedTrip.employee_id),
          checkinImage: rawBase64,
          notes: notes,
          date: new Date().toISOString(),
        });
        setSelectedCheckIn(novo);
        upsertCheckIn(novo);
        void fetchCheckIns();
      } catch (err: unknown) {
        setFormError(getApiErrorMessage(err, 'Erro ao iniciar check-in.'));
      }
      stopCamera();
    } else if (photo && currentPhotoType === 'fim' && showCheckoutModalFor !== null) {
      setCheckoutImageTemp(photo);
      stopCamera();
    } else if (photo && currentPhotoType === 'fim') {
      setFormError('Abra a finalização do check-in antes de tirar a foto.');
      stopCamera();
    }
  };

  const handleSaveCheckIn = async (imageOverride?: string | null) => {
    const imageToUse = imageOverride ?? checkoutImage ?? checkoutImageTemp;
    if (!selectedCheckIn) {
      setFormError('Você precisa iniciar o check-in antes de finalizar.');
      return;
    }
    if (!imageToUse) {
      setFormError('Tire a foto do check-out antes de finalizar.');
      return;
    }
    setFormError(null);
    try {
      const compressed = await resizeImage(imageToUse, 800, 800, 0.7);
      const rawBase64 = compressed.replace(/^data:image\/\w+;base64,/, '');
      const updated = await checkinService.create({
        checkinId: selectedCheckIn.id,
        checkoutImage: rawBase64,
      });
      closeCheckoutModal();
      setSelectedCheckIn(updated);
      setCheckinImage(null);
      setCheckoutImage(compressed);
      upsertCheckIn(updated);
      void fetchCheckIns();
    } catch (err: unknown) {
      setFormError(getApiErrorMessage(err, 'Erro ao finalizar check-in.'));
    }
  };

  const handleArchiveCheckIn = async (id: number) => {
    setArchivingId(id);
    try {
      const duration = timers[id] || 0;
      await checkinService.archiveCheckin(id, duration);
      void fetchCheckIns();
    } catch (_err) {
      setError('Erro ao arquivar check-in.');
    } finally {
      setArchivingId(null);
    }
  };

  const tripsEmAndamento = useMemo(
    () => trips.filter((t) => (t.status as string) === 'em_andamento'),
    [trips],
  );

  const ThumbnailImage = ({
    image,
    fallback,
    alt,
  }: {
    image?: CheckinImage | null;
    fallback: string;
    alt: string;
  }) => {
    const src = getCheckinImageSrc(image);
    if (!src) {
      return (
        <span className="inline-flex items-center rounded-md bg-slate-50 px-2 py-1 text-[11.5px] font-medium text-slate-400 ring-1 ring-inset ring-slate-200">
          <ImageIcon className="h-3 w-3 stroke-[1.9] mr-1" />
          {fallback}
        </span>
      );
    }
    return (
      <button
        type="button"
        onClick={() => openImage(image)}
        className="group relative block h-10 w-10 shrink-0 overflow-hidden rounded-lg border-2 border-white shadow-sm transition-all hover:z-10 hover:scale-110 hover:shadow-md"
        title={`Clique para ampliar a ${alt}`}
      >
        <img src={src} alt={alt} className="h-full w-full object-cover" />
      </button>
    );
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Check-ins do setor" hideTitleInBody />

        <div className="mb-7 flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5 max-w-2xl">
            <p className="text-[15px] font-semibold leading-tight text-sky-600">
              Operações — Check-In / Check-Out
            </p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight leading-tight text-slate-900">
                Controle de entrada e saída
              </h1>
              <span className="text-[18px] sm:text-[20px] font-medium text-slate-500 leading-tight">
                dos veículos do seu departamento
              </span>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
            <Button type="button" variant="primary" onClick={openCheckInModal} className="h-10 px-4">
              <Plus className="h-4 w-4 stroke-[2]" />
              Novo Check-In
            </Button>
            <Button variant="secondary" onClick={() => void fetchCheckIns()} className="h-10 px-4">
              <RefreshCw className="h-4 w-4 stroke-[1.8]" />
              Atualizar
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 text-white shadow-[0_6px_16px_-8px_rgba(16,185,129,0.6)]">
                <Navigation2 className="h-[18px] w-[18px] stroke-[1.9]" />
              </div>
              <div>
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                  Check-Ins
                </h2>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  {loading
                    ? 'Carregando registros...'
                    : pageRange.total === 0
                      ? 'Nenhum check-in encontrado'
                      : `${pageRange.start} a ${pageRange.end} de ${pageRange.total} check-ins listados (total cadastrado: ${checkIns.length})`}
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[360px] shrink-0 relative">
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 stroke-[1.8] text-slate-400"
                aria-hidden="true"
              />
              <Input
                type="text"
                name="search-checkins"
                placeholder="Pesquisar veículo, placa, motorista, notas ou status..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="!pl-10"
              />
            </div>
          </CardHeader>

          <CardBody className="!py-3">
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                <span className="ml-3 text-sm font-medium text-slate-600">Carregando check-ins...</span>
              </div>
            ) : isViewMode && selectedCheckIn ? (
              <div className="w-full max-w-5xl mx-auto">
                {(() => {
                  const v = resolveVehicle(selectedCheckIn);
                  const e = resolveEmployee(selectedCheckIn);
                  return (
                    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-slate-200 px-6 sm:px-8 py-5">
                        <div>
                          <h3 className="text-[20px] font-bold text-slate-900">Detalhes do Check-In</h3>
                          <p className="mt-1 text-[13px] text-slate-500">
                            Registro #{selectedCheckIn.id} • Atualizado em {formatDateTime(selectedCheckIn.updated_at)}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge tone={getStatusTone(selectedCheckIn.status)} className="gap-1.5 text-xs px-3 py-1.5">
                            {selectedCheckIn.status === 'finished' ? (
                              <CheckCircle2 className="h-3.5 w-3.5 stroke-[2]" />
                            ) : (
                              <Clock className="h-3.5 w-3.5 stroke-[1.9]" />
                            )}
                            {getStatusLabel(selectedCheckIn.status)}
                          </Badge>
                          <Button type="button" variant="secondary" onClick={() => { setIsViewMode(false); setSelectedCheckIn(null); }} className="h-9 px-4">
                            <ChevronLeft className="h-4 w-4 stroke-[1.9]" />
                            Voltar à Lista
                          </Button>
                        </div>
                      </div>
                      <div className="p-6 sm:p-8 grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Veículo</p>
                          <p className="mt-1.5 text-[14.5px] font-bold text-slate-900">{v?.brand || `Veículo #${selectedCheckIn.vehicle_id}`}</p>
                          <p className="mt-0.5 text-[12.5px] text-slate-600 font-mono">{v?.license_plate || '-'}</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Motorista</p>
                          <p className="mt-1.5 text-[14.5px] font-bold text-slate-900">{e?.name || `Funcionário #${selectedCheckIn.employee_id}`}</p>
                          <p className="mt-0.5 text-[12.5px] text-slate-600">{e?.cargo || '-'}</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Data / Hora Início</p>
                          <p className="mt-1.5 text-[14.5px] font-bold text-slate-900 tabular-nums">{formatDateTime(selectedCheckIn.date)}</p>
                          {selectedCheckIn.status === 'in_progress' && (
                            <p className="mt-1 text-[12px] font-mono font-bold text-amber-700">⏱ Duração: {formatTimer(timers[selectedCheckIn.id] || 0)}</p>
                          )}
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Notas / Observações</p>
                          <p className="mt-1.5 text-[14px] font-medium text-slate-800">{selectedCheckIn.notes || 'Sem observações cadastradas.'}</p>
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500 mb-2">Foto Check-In (Entrada)</p>
                          {(() => {
                            const img = (selectedCheckIn.images || []).find((i) => i.type === 'CHECKIN') || (selectedCheckIn.images || [])[0];
                            const src = getCheckinImageSrc(img);
                            return src ? (
                              <button
                                type="button"
                                onClick={() => openImage(img)}
                                className="group relative block w-full aspect-video overflow-hidden rounded-xl border-2 border-white shadow-md"
                              >
                                <img src={src} alt="Check-In" className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]" />
                              </button>
                            ) : (
                              <span className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-[13px] font-medium text-slate-500">
                                <ImageIcon className="h-4 w-4 stroke-[1.8]" />
                                Foto não disponível
                              </span>
                            );
                          })()}
                        </div>
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                          <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500 mb-2">Foto Check-Out (Saída)</p>
                          {(() => {
                            const img = (selectedCheckIn.images || []).find((i) => i.type === 'CHECKOUT') || (selectedCheckIn.images || [])[1];
                            const src = getCheckinImageSrc(img);
                            return src ? (
                              <button
                                type="button"
                                onClick={() => openImage(img)}
                                className="group relative block w-full aspect-video overflow-hidden rounded-xl border-2 border-white shadow-md"
                              >
                                <img src={src} alt="Check-Out" className="h-full w-full object-cover transition-transform group-hover:scale-[1.02]" />
                              </button>
                            ) : (
                              <span className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-3 text-[13px] font-medium text-slate-500">
                                <Clock className="h-4 w-4 stroke-[1.8]" />
                                Aguardando finalização do check-out
                              </span>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end border-t border-slate-200 px-6 sm:px-8 py-5">
                        {selectedCheckIn.status === 'finished' && (
                          <Button
                            type="button"
                            variant="secondary"
                            onClick={() => void handleArchiveCheckIn(selectedCheckIn.id)}
                            disabled={archivingId === selectedCheckIn.id}
                            className="h-10 px-5"
                          >
                            <Archive className="h-4 w-4 stroke-[1.9]" />
                            {archivingId === selectedCheckIn.id ? 'Arquivando...' : 'Arquivar Check-In'}
                          </Button>
                        )}
                        {selectedCheckIn.status === 'in_progress' && (
                          <Button
                            type="button"
                            variant="primary"
                            onClick={() => openCheckoutModal(selectedCheckIn)}
                            className="h-10 px-5 !bg-emerald-600 hover:!bg-emerald-700"
                          >
                            <BadgeCheck className="h-4 w-4 stroke-[2]" />
                            Finalizar Check-Out Agora
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <Fragment>
                <ModernTable>
                  <Thead>
                    <Tr>
                      <Th>Veículo</Th>
                      <Th>Motorista</Th>
                      <Th>Data / Hora</Th>
                      <Th>Notas</Th>
                      <Th>Check-In</Th>
                      <Th>Check-Out</Th>
                      <Th>Status</Th>
                      <Th>Ações</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {pageRange.total === 0 ? (
                      <tr>
                        <td colSpan={8} className="px-5 py-14 text-center text-sm text-slate-500">
                          Nenhum check-in encontrado
                        </td>
                      </tr>
                    ) : (
                      paginatedItems.map((c) => {
                        const v = resolveVehicle(c);
                        const e = resolveEmployee(c);
                        const imgIn = (c.images || []).find((i) => i.type === 'CHECKIN') || (c.images || [])[0];
                        const imgOut = (c.images || []).find((i) => i.type === 'CHECKOUT') || (c.images || [])[1];
                        return (
                          <Tr key={c.id}>
                            <Td>
                              <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 min-w-[40px] shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600 text-white shadow-[0_6px_16px_-8px_rgba(16,185,129,0.55)]">
                                  <Car className="h-[18px] w-[18px] min-h-[18px] min-w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="truncate font-semibold text-slate-900" title={v?.brand}>
                                    {v?.brand || `Veículo #${c.vehicle_id}`}
                                  </div>
                                  <div className="mt-0.5 font-mono text-[12px] tracking-wide text-slate-500 truncate">
                                    {v?.license_plate || '-'}
                                  </div>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-slate-500 ring-1 ring-inset ring-slate-200">
                                  <User className="h-4 w-4 stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="truncate font-semibold text-slate-900" title={e?.name}>
                                    {e?.name || `#${c.employee_id}`}
                                  </div>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-start gap-1.5">
                                <CalendarDays className="h-3.5 w-3.5 shrink-0 mt-0.5 stroke-[1.8] text-slate-400" />
                                <div className="flex flex-col leading-tight">
                                  <span className="font-semibold text-slate-700 tabular-nums text-[12.5px]">{formatDate(c.date)}</span>
                                  <span className="text-[11.5px] text-slate-500 tabular-nums">
                                    {new Date(c.date).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-start gap-1.5 w-full">
                                <AlignLeft className="h-3.5 w-3.5 shrink-0 mt-0.5 stroke-[1.8] text-slate-400" />
                                <span className="block truncate font-medium text-slate-700 max-w-[300px]" title={c.notes || '-'}>
                                  {c.notes || '-'}
                                </span>
                              </div>
                            </Td>
                            <Td>
                              <ThumbnailImage image={imgIn} fallback="Sem foto" alt="Foto de Check-In" />
                            </Td>
                            <Td>
                              <ThumbnailImage image={imgOut} fallback="Sem foto" alt="Foto de Check-Out" />
                            </Td>
                            <Td>
                              {c.status === 'in_progress' ? (
                                <div className="flex flex-col items-start gap-2">
                                  <Badge tone="amber" className="gap-1.5">
                                    <Clock className="h-3 w-3 stroke-[1.9]" />
                                    {getStatusLabel(c.status)}
                                  </Badge>
                                  <div className="inline-flex items-center gap-1 rounded-md bg-slate-50 px-2 py-0.5 text-[11.5px] font-bold font-mono text-amber-700 ring-1 ring-inset ring-slate-200 tabular-nums">
                                    ⏱ {formatTimer(timers[c.id] || 0)}
                                  </div>
                                  <Button
                                    type="button"
                                    variant="primary"
                                    size="sm"
                                    onClick={() => openCheckoutModal(c)}
                                    className="!h-8 !px-3 !bg-emerald-600 hover:!bg-emerald-700"
                                  >
                                    <BadgeCheck className="h-3.5 w-3.5 stroke-[2]" />
                                    Finalizar
                                  </Button>
                                </div>
                              ) : (
                                <Badge tone={getStatusTone(c.status)} className="gap-1.5">
                                  <CheckCircle2 className="h-3 w-3 stroke-[2]" />
                                  {getStatusLabel(c.status)}
                                </Badge>
                              )}
                            </Td>
                            <Td>
                              <div className="flex items-center gap-1.5">
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => { setSelectedCheckIn(c); setIsViewMode(true); }}
                                  className="!h-8 !px-2"
                                  title="Visualizar detalhes"
                                >
                                  <Eye className="h-4 w-4 stroke-[1.9]" />
                                </Button>
                                {c.status === 'finished' && (
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    onClick={() => void handleArchiveCheckIn(c.id)}
                                    disabled={archivingId === c.id}
                                    className="!h-8 !px-2"
                                    title="Arquivar check-in"
                                  >
                                    {archivingId === c.id ? (
                                      <Loader2 className="h-4 w-4 stroke-[1.9] animate-spin" />
                                    ) : (
                                      <Archive className="h-4 w-4 stroke-[1.9]" />
                                    )}
                                  </Button>
                                )}
                              </div>
                            </Td>
                          </Tr>
                        );
                      })
                    )}
                  </Tbody>
                </ModernTable>

                {!loading && (
                  <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-[13px] font-medium text-slate-500">
                      Mostrando <span className="font-semibold text-slate-700">{pageRange.start}-{pageRange.end}</span> de <span className="font-semibold text-slate-700">{pageRange.total}</span> resultados
                    </div>
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronsLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <div className="inline-flex items-center gap-1 mx-1">
                        {getPageNumbers.map((n, idx) =>
                          n === '...' ? (
                            <span key={`dots-${idx}`} className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]">…</span>
                          ) : (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setCurrentPage(n)}
                              className={`inline-flex h-9 min-w-[36px] items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all disabled:opacity-60 ${
                                n === currentPage
                                  ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                                  : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                              }`}
                            >
                              {n}
                            </button>
                          ),
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
                      >
                        <ChevronsRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                    </div>
                  </div>
                )}
              </Fragment>
            )}
          </CardBody>
        </SectionCard>
      </div>

      {showCheckInModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 px-4 py-6">
          <div className="flex min-h-full items-center justify-center">
            <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl">
              <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-8 py-5">
                <div>
                  <h2 className="text-[20px] font-bold text-slate-900">Novo Check-In</h2>
                  <p className="mt-1 text-[13px] text-slate-500">Selecione a viagem em andamento e registre a foto de entrada do veículo.</p>
                </div>
                <button type="button" onClick={() => setShowCheckInModal(false)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700">
                  <X className="h-4 w-4 stroke-[1.9]" />
                </button>
              </div>
              <div className="max-h-[80vh] overflow-y-auto p-8">
                {!selectedTrip ? (
                  <div className="space-y-5">
                    <div className="flex flex-col gap-2">
                      <label className="text-[12.5px] font-bold uppercase tracking-wide text-slate-600">
                        Selecione a Viagem <span className="text-rose-600">*</span>
                      </label>
                      <select
                        value=""
                        onChange={(e) => {
                          const trip = trips.find((t) => t.id === Number(e.target.value));
                          setSelectedTrip(trip || null);
                        }}
                        className="h-11 rounded-lg border border-slate-300 px-3.5 text-[14px] font-medium text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition"
                      >
                        <option value="" disabled>Selecione uma viagem em andamento...</option>
                        {tripsEmAndamento.map((trip) => {
                          const tv = vehicles.find((v) => v.id === trip.vehicle_id);
                          const te = employees.find((e) => e.id === trip.employee_id);
                          return (
                            <option key={trip.id} value={trip.id}>
                              [{trip.id}] {trip.destination} — {trip.purpose} • {tv?.brand || ''} ({tv?.license_plate || ''}) — {te?.name || ''}
                            </option>
                          );
                        })}
                      </select>
                      {tripsEmAndamento.length === 0 && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] font-medium text-amber-800">
                          <span className="font-bold">Atenção:</span> Não há nenhuma viagem com status "Em Andamento" para ser iniciado o check-in. Crie e/ou inicie uma viagem primeiro.
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-5">
                    <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5">
                      <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500 mb-2">Viagem selecionada</p>
                      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-[13.5px]">
                        <div>
                          <p className="font-bold uppercase text-[11px] tracking-wide text-slate-500">Destino / Propósito</p>
                          <p className="font-semibold text-slate-900">{selectedTrip.destination || '-'}</p>
                          <p className="text-slate-600">{selectedTrip.purpose || '-'}</p>
                        </div>
                        <div>
                          <p className="font-bold uppercase text-[11px] tracking-wide text-slate-500">Data / Saída Prevista</p>
                          <p className="font-semibold text-slate-900 tabular-nums">{formatDateTime(selectedTrip.start_date) || '-'}</p>
                        </div>
                        <div>
                          <p className="font-bold uppercase text-[11px] tracking-wide text-slate-500">Veículo</p>
                          <p className="font-semibold text-slate-900">
                            {vehicles.find((v) => v.id === selectedTrip.vehicle_id)?.brand || `Veículo #${selectedTrip.vehicle_id}`}
                          </p>
                          <p className="font-mono text-slate-600 text-[12.5px]">
                            {vehicles.find((v) => v.id === selectedTrip.vehicle_id)?.license_plate || '-'}
                          </p>
                        </div>
                        <div>
                          <p className="font-bold uppercase text-[11px] tracking-wide text-slate-500">Motorista</p>
                          <p className="font-semibold text-slate-900">
                            {employees.find((e) => e.id === selectedTrip.employee_id)?.name || `Funcionário #${selectedTrip.employee_id}`}
                          </p>
                          <p className="text-slate-600 text-[12.5px]">
                            {employees.find((e) => e.id === selectedTrip.employee_id)?.cargo || '-'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="text-[12.5px] font-bold uppercase tracking-wide text-slate-600">Notas / Observações (opcional)</label>
                      <textarea
                        rows={3}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Ex: Painel marcador de combustível em 3/4, pneus dianteiros ok, limpeza interior boa..."
                        className="rounded-lg border border-slate-300 px-3.5 py-3 text-[14px] font-medium text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30 transition resize-y"
                      />
                    </div>

                    <div className="flex flex-col gap-2">
                      <label className="text-[12.5px] font-bold uppercase tracking-wide text-slate-600">
                        Foto Check-In <span className="text-rose-600">*</span>
                      </label>
                      <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-5">
                        {checkinImage || capturedPhoto ? (
                          <div className="flex flex-col items-center gap-3">
                            <button type="button" onClick={() => openImage({ id: 0, base64: capturedPhoto || checkinImage || '', created_at: '', type: 'CHECKIN', url: capturedPhoto || checkinImage || undefined } as CheckinImage)} className="relative w-full max-w-sm rounded-xl overflow-hidden shadow-md border-2 border-white">
                              <img src={capturedPhoto || checkinImage || ''} alt="Pré-visualização Foto Check-In" className="w-full h-auto object-cover" />
                            </button>
                            <div className="flex items-center gap-2">
                              <Button type="button" variant="secondary" onClick={() => startCamera('inicio')}>
                                <Camera className="h-4 w-4 stroke-[1.9]" />
                                Retirar Nova Foto
                              </Button>
                              <Button type="button" variant="secondary" onClick={() => { setCheckinImage(null); setCapturedPhoto(null); }}>
                                <X className="h-4 w-4 stroke-[1.9]" />
                                Remover
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                            <Button type="button" variant="primary" onClick={() => startCamera('inicio')}>
                              <Camera className="h-4 w-4 stroke-[1.9]" />
                              Tirar Foto com Câmera
                            </Button>
                            <label className="cursor-pointer inline-flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-[14px] font-semibold text-slate-700 transition-all hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700">
                              <ImageIcon className="h-4 w-4 stroke-[1.9]" />
                              Fazer Upload de Imagem
                              <input type="file" accept="image/*" className="hidden" onChange={handleCheckinImageUpload} />
                            </label>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {formError && (
                  <div className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-medium text-rose-800">
                    {formError}
                  </div>
                )}

                <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-between sm:items-center">
                  <div className="text-[12px] text-slate-400">
                    {selectedTrip && (capturedPhoto || checkinImage) && (
                      <span className="inline-flex items-center gap-1 text-emerald-600 font-medium">
                        <BadgeCheck className="h-4 w-4 stroke-[2]" />
                        Pronto para iniciar o check-in.
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col-reverse gap-3 sm:flex-row">
                    <Button type="button" variant="secondary" onClick={() => setShowCheckInModal(false)} className="h-10 px-5">
                      Cancelar
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => void confirmPhoto()}
                      disabled={!selectedTrip || !(checkinImage || capturedPhoto)}
                      className="h-10 px-5"
                    >
                      <CheckCircle2 className="h-4 w-4 stroke-[2]" />
                      Iniciar Check-In
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCheckoutModalFor !== null && selectedCheckIn && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 px-4 py-6">
          <div className="flex min-h-full items-center justify-center">
            <div className="w-full max-w-xl rounded-2xl bg-white shadow-2xl">
              <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-8 py-5">
                <div>
                  <h2 className="text-[20px] font-bold text-slate-900">Finalizar Check-In (Check-Out)</h2>
                  <p className="mt-1 text-[13px] text-slate-500">Registro #{selectedCheckIn.id} • Tire a foto de saída do veículo.</p>
                </div>
                <button type="button" onClick={closeCheckoutModal} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700">
                  <X className="h-4 w-4 stroke-[1.9]" />
                </button>
              </div>
              <div className="max-h-[80vh] overflow-y-auto p-8">
                <div className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-5">
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 text-[13.5px]">
                    <div>
                      <p className="font-bold uppercase text-[11px] tracking-wide text-amber-700">⏱ Tempo total decorrido</p>
                      <p className="text-[16px] font-black text-amber-900 font-mono tabular-nums">
                        {formatTimer(timers[selectedCheckIn.id] || 0)}
                      </p>
                    </div>
                    <div>
                      <p className="font-bold uppercase text-[11px] tracking-wide text-slate-500">Status atual</p>
                      <div className="mt-0.5">
                        <Badge tone="amber"><Clock className="h-3 w-3" /> Em Andamento</Badge>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5 flex flex-col gap-2">
                  <label className="text-[12.5px] font-bold uppercase tracking-wide text-slate-600">
                    Foto Check-Out <span className="text-rose-600">*</span>
                  </label>
                  <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-5">
                    {checkoutImageTemp ? (
                      <div className="flex flex-col items-center gap-3">
                        <button type="button" onClick={() => openImage({ id: 0, base64: checkoutImageTemp, created_at: '', type: 'CHECKOUT', url: checkoutImageTemp } as CheckinImage)} className="relative w-full max-w-sm rounded-xl overflow-hidden shadow-md border-2 border-white">
                          <img src={checkoutImageTemp} alt="Pré-visualização Check-Out" className="w-full h-auto object-cover" />
                        </button>
                        <div className="flex items-center gap-2">
                          <Button type="button" variant="secondary" onClick={() => startCamera('fim')}>
                            <Camera className="h-4 w-4 stroke-[1.9]" />
                            Retirar Nova
                          </Button>
                          <Button type="button" variant="secondary" onClick={() => setCheckoutImageTemp(null)}>
                            <X className="h-4 w-4 stroke-[1.9]" />
                            Remover
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                        <Button type="button" variant="primary" onClick={() => startCamera('fim')}>
                          <Camera className="h-4 w-4 stroke-[1.9]" />
                          Tirar Foto com Câmera
                        </Button>
                        <label className="cursor-pointer inline-flex h-10 items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 text-[14px] font-semibold text-slate-700 transition-all hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700">
                          <ImageIcon className="h-4 w-4 stroke-[1.9]" />
                          Upload de Imagem
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              const reader = new FileReader();
                              reader.onload = (ev) => setCheckoutImageTemp(ev.target?.result as string);
                              reader.readAsDataURL(file);
                            }}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                {formError && (
                  <div className="mt-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] font-medium text-rose-800">
                    {formError}
                  </div>
                )}

                <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <Button type="button" variant="secondary" onClick={closeCheckoutModal} className="h-10 px-5">
                    Cancelar
                  </Button>
                  <Button type="button" variant="primary" onClick={() => void handleSaveCheckIn()} disabled={!checkoutImageTemp} className="h-10 px-5 !bg-emerald-600 hover:!bg-emerald-700">
                    <BadgeCheck className="h-4 w-4 stroke-[2]" />
                    Finalizar Check-In
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isCameraOpen && (
        <div className="fixed inset-0 z-[60] overflow-y-auto bg-black bg-opacity-75 px-4 py-6">
          <div className="flex min-h-full items-center justify-center">
            <div className="w-full max-w-2xl rounded-2xl bg-white shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]">
              <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-8 py-5">
                <div>
                  <h2 className="text-[20px] font-bold text-slate-900">
                    {currentPhotoType === 'inicio' ? '📸 Foto Check-In (Entrada)' : '📸 Foto Check-Out (Saída)'}
                  </h2>
                  <p className="mt-1 text-[13px] text-slate-500">
                    {cameraLoading ? 'Iniciando câmera, aguarde...' : 'Posicione o documento/veículo no enquadramento e clique em Capturar.'}
                  </p>
                </div>
                <button type="button" onClick={stopCamera} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700">
                  <X className="h-4 w-4 stroke-[1.9]" />
                </button>
              </div>
              <div className="p-8">
                <div className="relative overflow-hidden rounded-2xl bg-black shadow-inner border-4 border-slate-200">
                  <video ref={videoRef} autoPlay playsInline muted webkit-playsinline="true" x5-playsinline="true" className="w-full h-auto bg-black object-cover" />
                  <div className="pointer-events-none absolute inset-0 rounded-2xl ring-[6px] ring-inset ring-white/10" />
                </div>
                <canvas ref={canvasRef} style={{ display: 'none' }} />
                {capturedPhoto && (
                  <div className="mt-5 rounded-2xl border-4 border-white bg-white p-1 shadow-lg ring-1 ring-slate-200">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500 mb-2 px-2 pt-1">Foto Capturada</p>
                    <img src={capturedPhoto} alt="Foto Capturada" className="w-full h-auto rounded-xl" />
                  </div>
                )}
                <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="text-[12px] text-slate-500">
                    {cameraLoading && (
                      <span className="inline-flex items-center gap-2 text-amber-700 font-semibold">
                        <Loader2 className="h-4 w-4 stroke-[1.9] animate-spin" />
                        Iniciando câmera traseira (environment)...
                      </span>
                    )}
                  </div>
                  <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center">
                    <Button type="button" variant="secondary" onClick={stopCamera}>
                      <X className="h-4 w-4 stroke-[1.9]" />
                      Cancelar
                    </Button>
                    {!capturedPhoto && (
                      <Button type="button" variant="primary" onClick={capturePhoto} disabled={cameraLoading} className="!bg-blue-600 hover:!bg-blue-700">
                        <Camera className="h-4 w-4 stroke-[1.9]" />
                        {cameraLoading ? 'Carregando...' : 'Capturar Foto'}
                      </Button>
                    )}
                    {capturedPhoto && (
                      <Button type="button" variant="primary" onClick={() => void confirmPhoto()} className="!bg-emerald-600 hover:!bg-emerald-700">
                        <CheckCircle2 className="h-4 w-4 stroke-[2]" />
                        Usar Esta Foto
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {imageModalOpen && imageModalSrc && (
        <div
          className="fixed inset-0 z-[70] bg-black bg-opacity-80 flex items-center justify-center px-4 py-6 animate-[fadeIn_0.15s_ease-out]"
          onClick={() => { setImageModalOpen(false); setImageModalSrc(null); }}
        >
          <div className="relative w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => { setImageModalOpen(false); setImageModalSrc(null); }}
              className="absolute -top-3 -right-3 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-900 shadow-[0_10px_30px_-8px_rgba(15,23,42,0.4)] transition-transform hover:scale-110"
              aria-label="Fechar"
            >
              <X className="h-5 w-5 stroke-[2.1]" />
            </button>
            <img
              src={imageModalSrc}
              alt="Foto ampliada"
              className="max-h-[85vh] w-auto max-w-full mx-auto rounded-2xl shadow-[0_20px_60px_-12px_rgba(0,0,0,0.6)] object-contain"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckIn;
